/**
 * The on-prem licence WALL (ADR-0073 hardening).
 *
 * RESTRICTED entitlements refuse features one at a time while every page still
 * loads — which reads as "Tray is behaving oddly", not "this installation is not
 * licensed". So when the backend reports an enforcing build with no effective
 * licence, this replaces the ENTIRE authenticated app with one screen: activate.
 *
 * The backend enforces it independently (`LicenseLockMiddleware` answers 423 to
 * the API), so this is the humane face of the lock, never the lock itself — a
 * user who bypasses the SPA gets 423s, not data.
 *
 * Renders `children` untouched on every non-enforcing build (dev, SaaS cells,
 * the platform image), including while the status query is still in flight: the
 * app must not flash a licence wall at people who have no licence to give.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { licenseApi } from "@/lib/api-license";

export function LicenseGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  // Activation answers **200 with `activated:false`** when the server issued a token
  // the install cannot verify — a real case: the licence was minted by a Control whose
  // signing key is not the twin of the public key baked into this image. Reporting only
  // request FAILURES left the operator staring at an unchanged screen after a click that
  // "worked". A refusal must always say something.
  const [refused, setRefused] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ["license", "status"],
    queryFn: licenseApi.status,
    // Poll: a licence can also be delivered by the heartbeat, and expiry can fall
    // while the tab is open — the wall must appear and disappear on its own.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const activate = useMutation({
    mutationFn: (k: string) => licenseApi.activate(k),
    onSuccess: (fresh) => {
      qc.setQueryData(["license", "status"], fresh);
      if (!fresh.activated) {
        setRefused(
          fresh.state === "expired"
            ? t("license.wall.refusedExpired", "La licenza è scaduta.")
            : t(
                "license.wall.refusedInvalid",
                "Il server ha emesso la licenza, ma questa installazione non riesce a verificarla: la chiave non corrisponde. Contatta il fornitore.",
              ),
        );
        return;
      }
      setRefused(null);
      setKey("");
      // Everything was 423 a moment ago; drop the lot rather than guess which
      // queries are now allowed to succeed.
      void qc.invalidateQueries();
    },
  });

  const s = statusQ.data;
  const locked = s?.enforced === true && (!s.activated || s.state === "expired");
  if (!locked) return <>{children}</>;

  const expired = s?.state === "expired";
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-sm">
        <KeyRound className="mb-4 h-8 w-8 text-muted-foreground" />
        <h1 className="text-lg font-semibold">
          {expired
            ? t("license.wall.expiredTitle", "Licenza scaduta")
            : t("license.wall.title", "Installazione non attivata")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {expired
            ? t(
                "license.wall.expiredBody",
                "La licenza di questa installazione è scaduta e il periodo di tolleranza è terminato. Inserisci una licenza valida per riprendere.",
              )
            : t(
                "license.wall.body",
                "Questa installazione di Tray non è ancora attivata. Inserisci la chiave di attivazione che hai ricevuto.",
              )}
        </p>
        <form
          className="mt-6 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setRefused(null);
            if (key.trim()) activate.mutate(key.trim());
          }}
        >
          <Input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
            autoFocus
          />
          <Button type="submit" disabled={!key.trim() || activate.isPending}>
            {t("license.wall.activate", "Attiva")}
          </Button>
        </form>
        {refused ? <p className="mt-3 text-sm text-destructive">{refused}</p> : null}
        {activate.isError ? (
          <p className="mt-3 text-sm text-destructive">
            {activate.error instanceof ApiError
              ? activate.error.message
              : t("license.wall.failed", "Attivazione non riuscita.")}
          </p>
        ) : null}
        <p className="mt-6 text-xs text-muted-foreground">
          {t(
            "license.wall.help",
            "Se non hai una chiave, contatta il fornitore di questa installazione.",
          )}
        </p>
      </div>
    </div>
  );
}
